import HostedMembership from "../components/HostedMembership";
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const API_BASE =
  process.env.REACT_APP_API_URL || "http://localhost:8000";

const PAYMENT_MODE =
  process.env.REACT_APP_PAYMENT_MODE || "auto";

const RAZORPAY_KEY_ID =
  process.env.REACT_APP_RAZORPAY_KEY_ID || "";

const hasRazorpayApiKey =
  PAYMENT_MODE === "checkout" ||
  (
    PAYMENT_MODE === "auto" &&
    RAZORPAY_KEY_ID &&
    !RAZORPAY_KEY_ID.includes("")
  );

const IMPACT = [
  { amount: '₹25', impact: 'covers your quarterly e-journal subscription' },
  // { amount: '₹100', impact: 'covers monthly e-journal subscription' },
  // { amount: '₹300', impact: 'covers a monthly e-journal + Physical journal subscription' },
  { amount: '₹51', impact: 'covers monthly e-journal subscription' },
  { amount: '₹101', impact: 'covers a monthly e-journal + Physical journal subscription' },
  { amount: '₹750', impact: 'Opportunities to volunteer in community service drives' },
  { amount: '₹1,500', impact: 'Invitation to exclusive member events, webinars, cultural and spiritual events' },
  { amount: '₹5,000', impact: 'Eligibility for special member recognition programs' },
];

const MEMBERSHIP_PLANS = [
  {
    tier: '25',
    amount: 25,
    label: 'Basic',
    description: 'Quarterly e-journal subscription',
    features: ['E-journal access', 'Community updates', 'Priority volunteer invites'],
    popular: false
  },
  {
    tier: '51',
    amount: 51,
    label: 'Standard',
    description: 'Monthly e-journal subscription',
    features: ['Monthly e-journal', 'Physical journal option', 'Volunteer priority', 'Event invites'],
    popular: true
  },
  {
    tier: '101',
    amount: 101,
    label: 'Premium',
    description: 'Monthly e-journal + Physical journal',
    features: ['Monthly e-journal', 'Physical journal delivery', 'Volunteer priority', 'Exclusive events', 'Member recognition'],
    popular: false
  }
];

const MEMBERSHIP_BENEFITS = [
  {
    icon: '📚',
    title: 'Quarterly Journal Access',
    desc: 'Receive e-journals every quarter/month based on subscription, covering community welfare, nutrition research, and impact stories from the field.',
  },
  {
    icon: '📖',
    title: 'Physical Journal Option',
    desc: 'Choose printed journal delivery to your doorstep — in-depth articles, field reports, and member spotlights.',
  },
  {
    icon: '🤝',
    title: 'Food Campaign Participation',
    desc: 'Members get priority invites to volunteer at our food distribution drives — serve meals, meet families.',
  },
];

const PAYMENT_METHODS = [
  {
    id: 'card',
    title: 'Debit / Credit Card',
    subtitle: 'Monthly Membership',
    icon: '💳',
    description: 'Set up a monthly membership using your debit or credit card. You will be signing up for a recurring payment mandate.',
    steps: [
      'Enter your card details securely on Razorpay\'s checkout',
      'Authenticate via OTP sent to your registered mobile number',
      'Your card will be auto-debited every month on the same date',
      'Cancel anytime from your Razorpay dashboard or by contacting us',
    ],
    badge: 'Monthly',
    badgeColor: 'bg-green-100 text-green-700',
  },
  {
    id: 'upi',
    title: 'UPI QR Code',
    subtitle: 'One-Time Membership',
    icon: '📱',
    description: 'Scan a UPI QR code with any UPI app (GPay, PhonePe, Paytm, BHIM) and activate membership instantly.',
    steps: [
      'Click the button below to generate a unique UPI QR code',
      'Scan using your preferred UPI app',
      'Enter the amount you wish to contribute',
      'Complete the payment via your UPI PIN',
    ],
    badge: 'Instant',
    badgeColor: 'bg-blue-100 text-blue-700',
  },
  {
    id: 'enach',
    title: 'e-NACH / Bank Mandate',
    subtitle: 'Monthly Membership',
    icon: '🏦',
    description: 'Authorize automatic monthly membership payments directly from your bank account via e-NACH mandate.',
    steps: [
      'Enter your bank account number and IFSC code',
      'Authenticate via net-banking or debit card',
      'Sign the e-mandate digitally via OTP',
      'Automatic monthly debit from your bank account',
    ],
    badge: 'Monthly',
    badgeColor: 'bg-green-100 text-green-700',
  },
];

export default function Membership() {
  const ref = useRef(null);
  const [activeMethod, setActiveMethod] = useState('card');
  const [selectedAmount, setSelectedAmount] = useState(25);
  const [customAmount, setCustomAmount] = useState('');
  const [memberName, setMemberName] = useState('');
  const [memberEmail, setMemberEmail] = useState('');
  const [memberPhone, setMemberPhone] = useState('');
  const [isMonthly, setIsMonthly] = useState(true);
  const [showRecurringNotice, setShowRecurringNotice] = useState(false);
  const [loading, setLoading] = useState(false);
  const [donateError, setDonateError] = useState('');
  const [selectedTier, setSelectedTier] = useState('25'); // Default to ₹25 plan


  useEffect(() => {
    if (ref.current) {
      ref.current.classList.add('page-enter');
    }
  }, []);

  useEffect(() => {
    const method = PAYMENT_METHODS.find((m) => m.id === activeMethod);
    if (method && method.badge === 'Monthly') {
      setShowRecurringNotice(true);
    } else {
      setShowRecurringNotice(false);
    }
  }, [activeMethod]);

  const handleAmountSelect = (amt) => {
    setSelectedAmount(amt);
    setCustomAmount('');
  };

  const handleCustomAmountChange = (e) => {
    const val = e.target.value.replace(/[^0-9]/g, '');
    setCustomAmount(val);
    if (val) setSelectedAmount(parseInt(val, 10));
  };

  const handleDonate = async () => {
    const amount = selectedAmount || parseInt(customAmount || "0", 10);
    if (!amount || amount < 1) {
      alert("Please enter a valid membership amount.");
      return;
    }

    if (!memberName || !memberEmail || !memberPhone) {
      alert("Please fill all member details.");
      return;
    }

    if (!hasRazorpayApiKey) {
      return;
    }

    setDonateError('');
    setLoading(true);

    try {
      // For e-NACH monthly memberships, use backend subscription API
      // to get a full-page checkout URL (avoids X-Frame-Options block)
      if (activeMethod === 'enach') {
        const response = await fetch(`${API_BASE}/payment/subscription`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            memberName: memberName,
            memberEmail: memberEmail,
            memberPhone: memberPhone.replace(/\D/g, ""),
            tier: selectedTier
          })
        });

        const data = await response.json();
        if (!response.ok || !data.success) {
          throw new Error(data.detail || "Failed to create subscription.");
        }

        if (data.shortUrl) {
          localStorage.setItem("subscriptionId", data.subscriptionId);
          window.location.href = data.shortUrl;
        } else {
          throw new Error("No checkout URL received from server.");
        }
        setLoading(false);
        return;
      }

      // For card/UPI: use standard Razorpay order checkout
      const response = await fetch(
        `${API_BASE}/payment/order`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            amount,
            memberName,
            memberEmail,
            memberPhone,
            recurring: isMonthly,
            method: activeMethod
          })
        }
      );

      const orderData = await response.json();
      if (!orderData.success) {
        throw new Error("Order creation failed");
      }

      const options = {
        key: RAZORPAY_KEY_ID,
        amount: orderData.amount,
        currency: orderData.currency,
        order_id: orderData.orderId,
        name: "BSM Bhojan Seva Mandal Foundation",
        description: isMonthly ? `Monthly Membership ₹${amount}` : `Membership ₹${amount}`,
        image: "/logo.png",
        prefill: { name: memberName, email: memberEmail, contact: memberPhone },
        notes: { donor_name: memberName, donor_email: memberEmail, payment_method: activeMethod },
        theme: { color: "#f97316" },
        modal: { ondismiss: function () { setLoading(false); } }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (err) {
      console.error(err);
      setDonateError(err.message || "Unable to initiate payment.");
      setLoading(false);
    }
  };

  return (
    <div ref={ref} className="pt-20">
      {/* ── HERO ── */}
      <section className="bg-gradient-to-br from-orange-600 to-amber-700 py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <svg viewBox="0 0 400 400" className="w-full h-full">
            {[80, 120, 160, 200, 240, 280, 320].map((r) => (
              <circle key={r} cx="200" cy="200" r={r} fill="none" stroke="white" strokeWidth="0.5" />
            ))}
          </svg>
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <div className="font-hindi text-orange-200 text-xl mb-4">सदस्य बनें — Become a Member</div>
          <h1 className="font-display text-5xl md:text-6xl text-white mb-6">
            Join Our Seva Family
          </h1>
          <p className="text-orange-100 text-lg max-w-xl mx-auto">
            Become a member to receive journals on community welfare & nutrition. Your membership also gives you priority access to volunteer at our food campaigns.
          </p>
        </div>
      </section>

      {/* ── IMPACT ── */}
      <section className="py-20 bg-amber-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <div className="lotus-divider mb-4">🪷 🪷 🪷</div>
            <h2 className="section-title">Your Membership's Impact</h2>
            <p className="section-subtitle mx-auto">Together, our members make community service and engagement possible.</p>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
            {IMPACT.map(({ amount, impact }) => (
              <div key={amount} className="card p-5 flex gap-4 items-center">
                <span className="font-display text-2xl text-orange-600 flex-shrink-0 w-20 text-right">{amount}</span>
                <div className="w-px h-10 bg-amber-200 flex-shrink-0" />
                <span className="text-amber-800 text-sm leading-relaxed">{impact}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── MEMBERSHIP FORM & METHODS ── */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <div className="lotus-divider mb-4">🪷 🪷 🪷</div>
            <h2 className="section-title">Become a Member</h2>
            <p className="section-subtitle mx-auto">
              Choose your membership plan and payment method. All transactions are secured by Razorpay.
            </p>

          </div>

          <div className="grid lg:grid-cols-5 gap-12 items-start">
            {/* LEFT: Membership Form */}
            <div className="lg:col-span-3">
            {/* UPDATED CONDITION TO FALLBACK TO HOSTED IF NO API KEYS */}
            {PAYMENT_MODE === "hosted" || !hasRazorpayApiKey ? (
              <HostedMembership />
            ) : (
            <div className="space-y-8">
              {/* Amount Selection */}
              <div className="card p-6">
                <h3 className="font-display text-xl text-amber-900 mb-4">Membership Amount</h3>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mb-4">
                  {[25, 100, 300, 500, 750, 1000, 1500, 5000].map((amt) => (
                    <button
                      key={amt}
                      onClick={() => handleAmountSelect(amt)}
                      className={`py-3 px-4 rounded-sm font-semibold text-sm transition-all ${
                        selectedAmount === amt && !customAmount
                          ? 'bg-orange-600 text-white shadow-md'
                          : 'bg-amber-50 text-amber-900 hover:bg-orange-100 border border-amber-200'
                      }`}
                    >
                      ₹{amt}/mo
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-amber-700 font-medium">Custom ₹</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="Enter amount"
                    value={customAmount}
                    onChange={handleCustomAmountChange}
                    className="flex-1 border border-amber-200 rounded-sm px-4 py-2 text-amber-900 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Membership Plan Selection */}
              <div className="card p-6">
                <h3 className="font-display text-xl text-amber-900 mb-4">Choose Your Membership Plan</h3>
                <p className="text-sm text-amber-700 mb-6">Select a monthly membership plan that suits your support level. All plans are recurring monthly.</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {MEMBERSHIP_PLANS.map((plan) => (
                    <button
                      key={plan.tier}
                      onClick={() => {
                        setSelectedTier(plan.tier);
                        setSelectedAmount(plan.amount);
                        setCustomAmount('');
                      }}
                      className={`relative p-5 rounded-lg border-2 transition-all text-left ${
                        selectedTier === plan.tier
                          ? 'border-orange-600 bg-orange-50 shadow-lg'
                          : 'border-amber-200 bg-white hover:border-orange-300 hover:bg-amber-50'
                      }`}
                    >
                      {plan.popular && (
                        <span className="absolute -top-3 left-4 px-2 py-0.5 bg-orange-600 text-white text-xs font-semibold rounded-full">
                          Most Popular
                        </span>
                      )}
                      <div className="flex items-baseline gap-1 mb-2">
                        <span className="font-display text-3xl font-bold text-amber-900">₹{plan.amount}</span>
                        <span className="text-amber-600 text-sm">/month</span>
                      </div>
                      <h4 className="font-semibold text-amber-900 mb-1">{plan.label}</h4>
                      <p className="text-xs text-amber-600 mb-3">{plan.description}</p>
                      <ul className="space-y-1 text-sm text-amber-800">
                        {plan.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center gap-2">
                            <span className="w-4 h-4 flex-shrink-0 text-orange-600">✓</span>
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                      {selectedTier === plan.tier && (
                        <div className="absolute inset-0 border-2 border-orange-600 rounded-lg pointer-events-none" />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Member Details */}
              <div className="card p-6">
                <h3 className="font-display text-xl text-amber-900 mb-4">Member Details</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-amber-700 mb-1">Full Name *</label>
                    <input
                      type="text"
                      value={memberName}
                      onChange={(e) => setMemberName(e.target.value)}
                      className="w-full border border-amber-200 rounded-sm px-4 py-2 text-amber-900 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="Enter your full name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-amber-700 mb-1">Email Address *</label>
                    <input
                      type="email"
                      value={memberEmail}
                      onChange={(e) => setMemberEmail(e.target.value)}
                      className="w-full border border-amber-200 rounded-sm px-4 py-2 text-amber-900 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="Enter your email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-amber-700 mb-1">Phone Number *</label>
                    <input
                      type="tel"
                      value={memberPhone}
                      onChange={(e) => setMemberPhone(e.target.value)}
                      className="w-full border border-amber-200 rounded-sm px-4 py-2 text-amber-900 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="Enter your 10-digit mobile number"
                    />
                  </div>
                </div>
              </div>

              {/* Payment Method Tabs */}
              <div className="card p-6">
                <h3 className="font-display text-xl text-amber-900 mb-4">Membership Plan</h3>
                <div className="flex flex-wrap gap-2 mb-6">
                  {PAYMENT_METHODS.map((method) => (
                    <button
                      key={method.id}
                      onClick={() => setActiveMethod(method.id)}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-sm text-sm font-medium transition-all border ${
                        activeMethod === method.id
                          ? 'bg-orange-600 text-white border-orange-600 shadow-md'
                          : 'bg-white text-amber-800 border-amber-200 hover:bg-amber-50'
                      }`}
                    >
                      <span>{method.icon}</span>
                      <span>{method.title}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ml-1 ${method.badgeColor}`}>{method.subtitle}</span>
                    </button>
                  ))}
                </div>

                {PAYMENT_METHODS.map((method) =>
                  activeMethod === method.id ? (
                    <div key={method.id} className="space-y-4">
                      <div className="p-4 bg-amber-50 rounded-sm border border-amber-100">
                        <h4 className="font-semibold text-amber-900 mb-1">
                          {method.icon} {method.title} — {method.subtitle}
                        </h4>
                        <p className="text-sm text-amber-700">{method.description}</p>
                      </div>
                      <div>
                        <h5 className="font-semibold text-amber-900 text-sm mb-2">How it works:</h5>
                        <ol className="space-y-2">
                          {method.steps.map((step, i) => (
                            <li key={i} className="flex gap-3 text-sm text-amber-800">
                              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-orange-500 text-white text-xs flex items-center justify-center font-bold">
                                {i + 1}
                              </span>
                              {step}
                            </li>
                          ))}
                        </ol>
                      </div>
                    </div>
                  ) : null
                )}

                {showRecurringNotice && (
                  <div className="mt-6 p-4 bg-orange-50 border-l-4 border-orange-500 rounded-sm">
                    <h5 className="font-semibold text-orange-900 mb-2">⚠️ Monthly Membership Notice</h5>
                    <p className="text-sm text-orange-800 leading-relaxed">
                      You are signing up for a <strong>monthly membership mandate</strong>. Your selected payment method will be automatically debited every month on the same date for the chosen amount. You may cancel or pause this mandate at any time by contacting us or through your Razorpay dashboard. There are no penalties or lock-in periods.
                    </p>
                    <label className="flex items-center gap-2 mt-3 text-sm text-orange-800">
                      <input
                        type="checkbox"
                        checked={isMonthly}
                        onChange={(e) => setIsMonthly(e.target.checked)}
                        className="w-4 h-4 accent-orange-600"
                      />
                      I understand and agree to the monthly membership mandate
                    </label>
                  </div>
                )}

                {activeMethod === 'upi' && (
                  <div className="mt-6 p-4 bg-blue-50 border-l-4 border-blue-500 rounded-sm">
                    <h5 className="font-semibold text-blue-900 mb-2">ℹ️ One-Time Membership</h5>
                    <p className="text-sm text-blue-800 leading-relaxed">
                      UPI QR memberships are processed as one-time payments. If you wish to set up a monthly recurring membership, please choose Card or e-NACH instead, or return each month to renew via UPI.
                    </p>
                  </div>
                )}

                {donateError && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-sm text-sm text-red-700">
                    {donateError}
                  </div>
                )}

                <div className="mt-8">
                  <button
                    onClick={handleDonate}
                    disabled={loading}
                    className="w-full btn-primary py-4 text-base flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <span className="inline-block w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <span>❤️</span> Join ₹{selectedAmount || customAmount || 0} {isMonthly && activeMethod !== 'upi' ? '/ month' : 'now'}
                      </>
                    )}
                  </button>
                  <p className="text-xs text-amber-500 text-center mt-4">
                    🔒 Secured by Razorpay. We do not store your card or bank details on our servers.
                  </p>
                </div>
              </div>
            </div>
            )}
            </div>

            {/* RIGHT: Benefits & Info */}
            <div className="lg:col-span-2 space-y-6">
              <h3 className="font-display text-2xl text-amber-900">Why Become a Member?</h3>
              <div className="space-y-4">
                {MEMBERSHIP_BENEFITS.map(({ icon, title, desc }) => (
                  <div key={title} className="card p-5 flex gap-4 hover:-translate-y-0.5 transition-transform duration-200">
                    <span className="text-3xl flex-shrink-0">{icon}</span>
                    <div>
                      <h4 className="font-semibold text-amber-900 mb-1">{title}</h4>
                      <p className="text-amber-700 text-sm leading-relaxed">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 p-5 bg-orange-50 border-l-4 border-orange-500 rounded-sm">
                <h4 className="font-display text-lg text-amber-900 mb-3">80G Tax Benefit</h4>
                <p className="text-sm text-amber-800 leading-relaxed">
                  All membership enrollments are eligible for tax deduction under Section 80G of the Income Tax Act. You will receive a receipt via email within 7 working days of your payment.
                </p>
              </div>

              <div className="p-5 bg-amber-100 border border-amber-200 rounded-sm">
                <h4 className="font-display text-lg text-amber-900 mb-3">Need Help?</h4>
                <p className="text-sm text-amber-800 mb-3">
                  If you face any issues with your membership or have questions about payment methods, feel free to reach out.
                </p>
                <a href="tel:+919081795075" className="text-sm text-orange-600 font-medium hover:underline">
                  📞 +91 90817 95075
                </a>
                <br />
                <a href="mailto:bsmbhojansevamandalfoundation@gmail.com" className="text-sm text-orange-600 font-medium hover:underline">
                  ✉️ bsmbhojansevamandalfoundation@gmail.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── TRUST SECTION ── */}
      <section className="py-12 bg-amber-900 text-center">
        <div className="max-w-3xl mx-auto px-6">
          <p className="text-amber-300 text-sm leading-relaxed">
            BSM Bhojan Seva Mandal Foundation is a registered Non-Profit Organization.
            All online membership payments are processed securely through Razorpay — India's leading payment gateway.
            Your personal and financial information is protected using industry-standard security practices.
          </p>
          <div className="flex justify-center gap-6 mt-6 text-amber-400 text-sm flex-wrap">
            <span>🔒 PCI-DSS Compliant</span>
            <span>📋 80G Certified</span>
            <span>🏦 RBI Regulated Gateway</span>
          </div>
        </div>
      </section>
    </div>
  );
}